<script setup>
// S16 — Sync run history. Read-only list of durable runs (paginated by the
// backend, never `all()`). Shows type, phase, status, attempt, timestamps and
// redacted error summary. Retry is offered on failed/partial runs.
import { ref, computed, onBeforeMount } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useShopifyStore } from "@/stores/shopify";
import moment from "moment";

const props = defineProps({
  integrationId: { type: String, required: true },
});

const shopifyStore = useShopifyStore();
const company_id = useCompanyStore().companyId;

const loading = ref(false);
const retryingId = ref("");

const runs = computed(() => shopifyStore.runs);

onBeforeMount(load);

async function load() {
  loading.value = true;
  try {
    await shopifyStore.fetchRuns(company_id, props.integrationId, { limit: 20 });
  } finally {
    loading.value = false;
  }
}

function fmt(ts) {
  return ts ? moment(ts).format("DD/MM/YYYY HH:mm") : "—";
}

const runTypeLabels = Object.freeze({
  discovery: "Location discovery",
  snapshot: "Catalogue synchronization",
  delta: "Catalogue update",
  reconcile: "Catalogue reconciliation",
});

const phaseLabels = Object.freeze({
  authorised: "Authorizing",
  queued: "Queued",
  discovering: "Discovering locations",
  exporting: "Preparing catalogue",
  transforming: "Preparing products",
  importing: "Updating catalogue",
  indexing: "Making products searchable",
  applying_changes: "Applying changes",
  ready: "Complete",
  failed: "Failed",
});

const statusLabels = Object.freeze({
  queued: "Queued",
  running: "In progress",
  succeeded: "Complete",
  partial: "Needs attention",
  failed: "Failed",
});

function labelFor(labels, value, fallback = "Needs review") {
  return labels[value] || fallback;
}

function readySiteCount(run) {
  return (run.per_site || []).filter((site) => site.status === "succeeded").length;
}

function retryable(run) {
  return run.status === "failed" || run.status === "partial";
}

async function retry(run) {
  retryingId.value = run.id;
  try {
    await shopifyStore.retryRun(company_id, props.integrationId, run.id);
    await load();
  } catch (e) {
    /* toast handled in store */
  } finally {
    retryingId.value = "";
  }
}

defineExpose({ runs, load, retry, retryable, labelFor, readySiteCount });
</script>

<template>
  <div class="run-history" data-test="run-history">
    <div class="run-history__head">
      <h3>Run history</h3>
      <button type="button" class="btn btn-secondary btn-sm" @click="load" :disabled="loading">
        Refresh
      </button>
    </div>

    <div v-if="loading && !runs.length" class="shopify-center" data-test="runs-loading">
      <span class="spinner-sm" role="status" aria-hidden="true"></span>
    </div>

    <p v-else-if="!runs.length" class="run-history__empty" data-test="runs-empty">
      No runs yet.
    </p>

    <table v-else class="run-table">
      <thead>
        <tr>
          <th>Type</th><th>Phase</th><th>Status</th><th>Attempt</th>
          <th>Started</th><th>Finished</th><th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="run in runs" :key="run.id" :data-test="`run-${run.id}`">
          <td>{{ labelFor(runTypeLabels, run.run_type) }}</td>
          <td>{{ labelFor(phaseLabels, run.phase) }}</td>
          <td>
            <span class="run-badge" :class="`run-badge--${run.status}`">{{ labelFor(statusLabels, run.status) }}</span>
          </td>
          <td>{{ run.attempt || 1 }}</td>
          <td>{{ fmt(run.started_at) }}</td>
          <td>{{ fmt(run.finished_at) }}</td>
          <td>
            <button
              v-if="retryable(run)"
              type="button" class="btn btn-secondary btn-sm"
              :disabled="retryingId === run.id"
              @click="retry(run)"
              :data-test="`retry-run-${run.id}`"
            >
              Retry
              <span v-if="retryingId === run.id" class="spinner-sm" role="status" aria-hidden="true"></span>
            </button>
          </td>
        </tr>
        <tr v-for="run in runs.filter((item) => item.per_site && item.per_site.length)" :key="`${run.id}-sites`" class="run-sites">
          <td colspan="7">
            <details>
              <summary>
                {{ readySiteCount(run) }}/{{ run.per_site.length }} Ravo Site<span v-if="run.per_site.length !== 1">s</span> complete
              </summary>
              <ul>
                <li v-for="site in run.per_site" :key="site.ravo_site_id">
                  <strong>{{ site.site_name || "Ravo Site" }}</strong>
                  <span>{{ labelFor(statusLabels, site.status) }}</span>
                </li>
              </ul>
              <p v-if="run.error_summary?.code" class="run-sites__diagnostic">
                Support code: {{ run.error_summary.code }}
              </p>
            </details>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.run-history__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.run-history__empty { color: var(--neutral-500); font-size: var(--text-sm); }
.shopify-center { text-align: center; padding: 16px 0; }
.run-table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
.run-table th, .run-table td { text-align: left; padding: 8px; border-bottom: 1px solid var(--neutral-200); }
.run-badge { font-size: 11px; padding: 1px 8px; border-radius: 100px; background: var(--neutral-100); }
.run-badge--succeeded { background: var(--green-50, #f0fdf4); color: var(--green-700, #15803d); }
.run-badge--failed { background: var(--red-50, #fef2f2); color: var(--red-700, #b91c1c); }
.run-badge--partial { background: var(--amber-100, #fef3c7); color: var(--amber-700, #b45309); }
.run-badge--running, .run-badge--queued { background: var(--primary-50); color: var(--primary-700); }
.btn-sm { padding: 4px 12px; font-size: 12px; }
.run-sites td { padding-top: 0; border-bottom: 1px solid var(--neutral-200); }
.run-sites details { color: var(--neutral-700); font-size: 12px; }
.run-sites summary { cursor: pointer; }
.run-sites ul { display: grid; gap: 4px; margin: 8px 0 0; padding-left: 18px; }
.run-sites li { display: flex; gap: 8px; }
.run-sites__diagnostic { color: var(--neutral-500); margin: 8px 0 0; }
</style>
